import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { RightArrowIcon, LeftArrowIcon } from '@components/icons';
import { postsPerPage } from '@config';
import { useMediaQuery } from '@hooks';

const StyledPaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 50px;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 8px;

  .pagination-item {
    ${({ theme }) => theme.mixins.button};

    background-color: transparent;
    border: 1px solid transparent;
    color: var(--slate);

    &:hover:not(.currentPage):not(:disabled) {
      color: var(--green);
    }
   
    &:hover:not(.currentPage):not(:disabled):not(.ellipsis) {
      color: var(--green);
    }

    &:focus:not(.currentPage):not(:disabled):not(.ellipsis) {
      outline: none;
      border-color: var(--green);
      color: var(--green);
    }
   
    &.currentPage {
      font-weight: 600;
      color: var(--green);
      border: 1px solid var(--green);
      background-color: var(--green-tint);
    }

    &:disabled:not(.currentPage):not(.ellipsis) {
      color: var(--dark-slate);
      cursor: not-allowed;
      opacity: 0.5;
      // background-color: transparent; /* Ensure no background from mixin's hover */
      // border-color: transparent;

      &:hover, &:focus {
        color: var(--dark-slate);
        background-color: transparent;
        border-color: transparent;
      }
    }
  }

  .arrow-button {
    svg {
      width: 1.5em;
      height: 1.5em;
      display: block;
    }
  }

  .pagination-item.ellipsis {
    cursor: default;
    color: var(--slate);
    background-color: transparent !important;
    border-color: transparent !important;
    &:hover {
      color: var(--slate);
      background-color: transparent;
    }
  }
`;
const StyledSearchBarContainer = styled.div`
  ${({ theme }) => theme.mixins.searchBar};
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  span {
    white-space: nowrap;
  }
`;

const ELLIPSIS_STRING = '...';
const DELTA = 2;
const MAX_PAGES = 7;

const PensievePagination = ({ filteredPosts, currentPage, setCurrentPage }) => {
  const [jumpPage, setJumpPage] = useState(currentPage);
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);
  const isMobileView = useMediaQuery('(max-width: 768px)');

  const handlePageChange = pageNumber => {
    const newPage = Math.max(1, Math.min(pageNumber, totalPages));
    setCurrentPage(newPage);
    setJumpPage(newPage);
  };

  const handleEllipsisClick = ellipsisIndex => {
    const pageJumpAmount = (DELTA * 2 + 1);
    const direction = (ellipsisIndex > currentPage ? 1 : -1);
    const targetPage = currentPage + (pageJumpAmount * direction);
    const newPage = Math.max(1, Math.min(targetPage, totalPages));

    setCurrentPage(newPage);
  };

  const pageNumbersToDisplay = useMemo(() => {
    if (totalPages <= 1) {
      return totalPages === 1 ? [1] : [];
    }
    if (totalPages <= MAX_PAGES) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages = [];
    let middleBlockStart;
    let middleBlockEnd;

    if (currentPage <= DELTA + 1) {
      middleBlockStart = 2;
      middleBlockEnd = Math.min(1 + DELTA * 2, totalPages - 1);
    } else if (currentPage >= totalPages - DELTA) {
      middleBlockEnd = totalPages - 1;
      middleBlockStart = Math.max(2, totalPages - 1 - (DELTA * 2));
    } else {
      middleBlockStart = currentPage - DELTA;
      middleBlockEnd = currentPage + DELTA;
    }

    const addPage = pageNumber => {
      if (pages.length === 0 || pages[pages.length - 1] !== pageNumber) {
        if (!pages.includes(pageNumber)) {
          pages.push(pageNumber);
        } else if (pages[pages.length - 1] !== pageNumber && pages[pages.length - 1] === ELLIPSIS_STRING) {
          pages.push(pageNumber);
        }
      }
    };
    const addEllipsis = () => {
      if (pages.length > 0 && pages[pages.length - 1] !== ELLIPSIS_STRING) {
        pages.push(ELLIPSIS_STRING);
      }
    };

    addPage(1);
    if (middleBlockStart > 2) {
      addEllipsis();
    }
    for (let i = middleBlockStart; i <= middleBlockEnd; i++) {
      addPage(i);
    }
    if (middleBlockEnd < totalPages - 1) {
      addEllipsis();
    }
    if (totalPages > 1) {
      addPage(totalPages);
    }

    const finalPages = [];
    const seenNumbers = new Set();
    for (const item of pages) {
      if (item === ELLIPSIS_STRING) {
        if (finalPages.length === 0 || finalPages[finalPages.length - 1] !== ELLIPSIS_STRING) {
          finalPages.push(item);
        }
      } else {
        if (!seenNumbers.has(item)) {
          finalPages.push(item);
          seenNumbers.add(item);
        }
      }
    }

    return finalPages;
  }, [currentPage, totalPages]);


  if (totalPages <= 1 && !filteredPosts.length ) {
    return null;
  }
  if (totalPages === 0 && filteredPosts.length === 0) {
    return null;
  }

  return (
    <React.Fragment>
      {totalPages > 1 && (
        <StyledPaginationContainer>
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="pagination-item arrow-button"
            aria-label="Go to previous page">
            <LeftArrowIcon />
          </button>

          {isMobileView ?
            <StyledSearchBarContainer>
              <input
                type="number"
                placeholder={currentPage}
                value={jumpPage}
                onChange={e => setJumpPage(Number(e.target.value))}
                onBlur={() => handlePageChange(jumpPage)}
                className="pagination-item"
                min={1}
                max={totalPages}
                aria-label={`Go to page ${currentPage}`}
              />
              <span>of {totalPages}</span>
            </StyledSearchBarContainer> :
            pageNumbersToDisplay.map((item, index) => {
              if (item === ELLIPSIS_STRING) {
                return (
                  <span
                    onClick={() => handleEllipsisClick(index)}
                    key={`ellipsis-${index}`}
                    className="pagination-item ellipsis"
                    aria-hidden="true">
                    {ELLIPSIS_STRING}
                  </span>
                );
              }
              const pageNumber = Number(item);
              return (
                <button
                  key={pageNumber}
                  onClick={() => handlePageChange(pageNumber)}
                  className={`pagination-item ${currentPage === pageNumber ? 'currentPage' : ''}`}
                  disabled={currentPage === pageNumber}
                  aria-current={currentPage === pageNumber ? 'page' : undefined}
                  aria-label={`Go to page ${pageNumber}`}>
                  {pageNumber}
                </button>
              );
            })
          }

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="pagination-item arrow-button"
            aria-label="Go to next page">
            <RightArrowIcon />
          </button>
        </StyledPaginationContainer>
      )}
    </React.Fragment>
  );
};

PensievePagination.propTypes = {
  filteredPosts: PropTypes.array.isRequired,
  currentPage: PropTypes.number.isRequired,
  setCurrentPage: PropTypes.func.isRequired,
};

export default PensievePagination;